import { useMemo } from "react";
import { Link } from "react-router-dom";

import { AppShell, Divider, Image, ScrollArea, Stack } from "@mantine/core";

import MosaicChat from "modules/chat/components/MosaicChat";
import MosaicButton from "./MosaicButton";
import UserFeature from "modules/user/config/features/UserFeature";
import SideMenuGroup from "./SideMenuGroup";
import SideMenuLink from "./SideMenuLink";

import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";
import { useAuthUserQuery } from "modules/user/hooks/queries/useAuthUserQuery";
import { usePermissions } from "modules/user/config/permissions/Can";

import { menuItems } from "./constants";
import { coreRoutesConstants } from "modules/core/routes/routesConstants";

import EfestraLogo from "../../assets/img/efestra-logo.png";

import { UserFeatureModuleKey, UserRole } from "modules/user/types/entities";

const SideMenu = () => {
  const { selectedProject, isSingleProject } = useSelectedProject();
  const { data: userData } = useAuthUserQuery();

  const { can } = usePermissions();

  const userRole = useMemo((): UserRole | null => {
    if (!userData) return null;

    if (isSingleProject) {
      return selectedProject?.user_role?.at(0) || userData?.best_role;
    }

    return userData?.best_role;
  }, [isSingleProject, selectedProject, userData]);

  const items = useMemo(
    () => menuItems({ can, projectId: selectedProject?.id || "all" }),
    [can, selectedProject?.id]
  );

  return (
    <AppShell.Navbar bg="black" c="white">
      <AppShell.Section p="lg" pb="xs">
        <Link to={coreRoutesConstants.dashboard.href("all")}>
          <Image src={EfestraLogo} alt="Efestra" w={104} fit="contain" />
        </Link>
      </AppShell.Section>

      <AppShell.Section
        grow
        component={ScrollArea}
        my="md"
        px="sm"
        type="never"
      >
        <Stack>
          <UserFeature featureKey={UserFeatureModuleKey.AI_CHAT}>
            <div>
              <Divider color="dark" mb={4} />

              <MosaicChat trigger={<MosaicButton />} />

              <Divider color="dark" mt={4} />
            </div>
          </UserFeature>

          <Stack gap={2}>
            {items.top.map(link => (
              <SideMenuLink key={link.label} {...link} />
            ))}
          </Stack>

          <Divider color="dark" />

          <Stack gap="xs">
            {items.groups.map(({ label, links, roles }) => {
              const isDefaultOpen = userRole && roles.includes(userRole);

              return (
                <SideMenuGroup
                  key={label}
                  label={label}
                  links={links}
                  defaultOpened={!!isDefaultOpen}
                />
              );
            })}
          </Stack>

          <Divider color="dark" />

          <Stack gap={2}>
            {items.bottom.map(link => (
              <SideMenuLink key={link.label} {...link} />
            ))}
          </Stack>
        </Stack>
      </AppShell.Section>
    </AppShell.Navbar>
  );
};

export default SideMenu;
